<template>
  <span
    v-if="isVisible"
    class="network-connectivity-status-icon"
    :role="liveRegionRole"
    :aria-live="liveRegionPoliteness"
    :aria-label="statusLabel"
    data-testid="network-connectivity-status-icon"
  >
    <span
      class="network-connectivity-status-icon__glyph material-symbols-rounded"
      :style="{ color: statusConfig.color }"
      aria-hidden="true"
      data-testid="network-connectivity-status-icon-glyph"
    >
      {{ statusConfig.icon }}
    </span>
  </span>
</template>

<script>
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { useRoute } from "vue-router"
import { useStore } from "vuex"
import networkConnectivityConfig from "@/configs/networkConnectivityConfig.js"

const NETWORK_STATUS_ROUTE_NAMES = Object.freeze([
  "School Dashboard",
  "Advanced Reporting"
])

const STATUS_BY_KIND = Object.freeze({
  offline: Object.freeze({
    icon: "wifi_off",
    color: "#B41700"
  }),
  slow: Object.freeze({
    icon: "android_wifi_3_bar_alert",
    color: "#716800"
  }),
  online: Object.freeze({
    icon: "wifi",
    color: "#005CB8"
  })
})

const isNetworkStatusRoute = (routeName) =>
  NETWORK_STATUS_ROUTE_NAMES.includes(routeName)

export default {
  name: "NetworkConnectivityStatusIcon",

  setup() {
    const { t } = useI18n()
    const route = useRoute()
    const store = useStore()

    const isVisible = computed(
      () =>
        networkConnectivityConfig.speedStatusEnabled &&
        isNetworkStatusRoute(route.name)
    )

    const networkIssueKind = computed(
      () => store.getters["networkConnectivity/networkIssueKind"]
    )

    const statusConfig = computed(() => {
      const kind = networkIssueKind.value
      if (kind === "offline") {
        return STATUS_BY_KIND.offline
      }
      if (kind === "slow") {
        return STATUS_BY_KIND.slow
      }
      return STATUS_BY_KIND.online
    })

    const statusLabel = computed(() => {
      const kind = networkIssueKind.value
      if (kind === "offline") {
        return t("network_connectivity.offline_message")
      }
      if (kind === "slow") {
        return t("network_connectivity.slow_message")
      }
      return t("network_connectivity.status_online_message")
    })

    const liveRegionRole = computed(() => {
      const kind = networkIssueKind.value
      if (kind === "offline" || kind === "slow") {
        return "alert"
      }
      return "status"
    })

    const liveRegionPoliteness = computed(() => {
      const kind = networkIssueKind.value
      if (kind === "offline" || kind === "slow") {
        return "assertive"
      }
      return "polite"
    })

    return {
      isVisible,
      statusConfig,
      statusLabel,
      liveRegionRole,
      liveRegionPoliteness
    }
  }
}
</script>

<style lang="scss" scoped>
.network-connectivity-status-icon {
  display: inline-flex;
  flex-shrink: 0;
  line-height: 1;
}

.network-connectivity-status-icon__glyph {
  font-size: 24px;
  line-height: 1;
  user-select: none;
}
</style>
